import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../lib/api'

interface User {
  id: string
  email: string
  name: string | null
  role: string
}

interface WalletState {
  address: string | null
  balance: string
  chainId: number | null
  isConnected: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isHydrated: boolean
  wallet: WalletState
  login: (user: User, token: string) => void
  logout: () => Promise<void>
  connectWallet: (address: string, balance: string, chainId: number) => void
  disconnectWallet: () => void
  updateWalletBalance: (balance: string) => void
  checkSession: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isHydrated: false,
      wallet: {
        address: null,
        balance: '0',
        chainId: null,
        isConnected: false,
      },
      login: (user, token) => set({ user, token, isAuthenticated: true }),
      logout: async () => {
        try {
          await api.post('/auth/logout')
        } catch (error) {
          console.error('Logout error:', error)
        }
        set({ 
          user: null, 
          token: null,
          isAuthenticated: false,
          wallet: { address: null, balance: '0', chainId: null, isConnected: false }
        })
      },
      checkSession: async () => {
        try {
          const response = await api.get('/auth/me')
          if (response.data.user) {
            set({ user: response.data.user, token: response.data.token || null, isAuthenticated: true })
          }
        } catch (error) {
          set({ user: null, token: null, isAuthenticated: false })
        }
      },
      connectWallet: (address, balance, chainId) => 
        set({ 
          wallet: { address, balance, chainId, isConnected: true }
        }),
      disconnectWallet: () => 
        set({ 
          wallet: { address: null, balance: '0', chainId: null, isConnected: false }
        }),
      updateWalletBalance: (balance) => 
        set((state) => ({ 
          wallet: { ...state.wallet, balance }
        })),
    }),
    {
      name: 'auth-storage',
      // Persist auth state including token
      partialize: (state) => ({ 
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        wallet: state.wallet 
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isHydrated = true
        }
      },
    }
  )
)
